// why.tsx — the one question the category never asks.
//
// A COMPLETED day tells you almost nothing. You meant to walk, you walked; the square fills and no
// one learns anything. The MISS is the only event in a habit tracker that carries new information,
// and every competitor throws it away: Streaks resets a number, HabitKit leaves a gap in the grid,
// Way of Life gives you a free-text diary and leaves the reading to you.
//
// So: after a miss, ONE tap-sized question, once. The answers are deliberately a small fixed set
// rather than free text — not to save the customer typing, but because a fixed set is COUNTABLE.
// Five words typed five different ways can never become "four of your five missed walks were days
// you worked late", and that sentence is the entire point of collecting this.
//
// Rules this surface holds itself to:
//   · at most ONE question on screen, ever — a tracker that interrogates you gets deleted
//   · only for a day in the last week, so it is still answerable from memory
//   · "Skip" is a real answer that is remembered, so the same day is never asked about twice
//   · never for today or the future; the day has to be over to have been missed

import { useEffect, useState } from "react";
import { store, type Entry, type Habit, type MissReason } from "./store";
import { expectsDay } from "./ledger";

/** The fixed vocabulary. Short, plain, and none of them blame the customer. */
export const REASONS = [
  "No time",
  "Too tired",
  "Forgot",
  "Away from home",
  "Wasn't feeling it",
] as const;

/** Recorded when the customer declines to answer — so we stop asking about that day. */
export const DECLINED = "—";

const DAY = 86_400_000;
const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);

export interface Ask {
  habit: Habit;
  day: string;
  /** True when this is a habit being BROKEN — the event is a slip, not an absence. */
  slip: boolean;
}

/**
 * Find the single most recent thing worth asking about, or null.
 *
 * Looks back a week, most recent first, and returns the FIRST unanswered event. Most recent wins
 * because it is the one the customer can still actually remember.
 */
export function findAsk(
  habits: Habit[],
  entriesByHabit: Record<string, Entry[]>,
  reasons: MissReason[],
  today: string,
): Ask | null {
  // ONE ANSWER PER DAY, full stop. Without this, answering surfaces the next unanswered miss
  // immediately — so a customer coming back after a bad week is asked seven times in a row, which
  // is an interrogation, not a question. Derived from the record's own timestamps rather than a
  // new preference: whatever they told us today is enough for today.
  if (reasons.some((r) => r.created_at.slice(0, 10) === today)) return null;

  const answered = new Set(reasons.map((r) => `${r.habit_id}:${r.on_day.slice(0, 10)}`));
  for (let back = 1; back <= 7; back++) {
    const day = iso(new Date(Date.parse(today + "T00:00:00") - back * DAY));
    for (const h of habits) {
      if (answered.has(`${h.id}:${day}`)) continue;
      const entries = entriesByHabit[h.id] ?? [];
      const marked = entries.some((e) => e.logged_on.slice(0, 10) === day);
      if (h.created_at && day < h.created_at.slice(0, 10)) continue; // before they started

      if (h.polarity === "break") {
        // A mark IS the event here. Understanding a slip is worth more than understanding a miss.
        if (marked) return { habit: h, day, slip: true };
        continue;
      }
      // A build habit: an absence only counts on a day its schedule actually expected.
      if (!marked && expectsDay(h.schedule, day)) return { habit: h, day, slip: false };
    }
  }
  return null;
}

function sayDay(day: string, today: string): string {
  const diff = Math.round((Date.parse(today + "T00:00:00") - Date.parse(day + "T00:00:00")) / DAY);
  if (diff === 1) return "Yesterday";
  const names = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return names[new Date(day + "T00:00:00").getDay()];
}

export function WhyPrompt({ habits, entriesByHabit, today, onRecorded }: {
  habits: Habit[];
  entriesByHabit: Record<string, Entry[]>;
  today: string;
  onRecorded: () => void | Promise<void>;
}) {
  const [reasons, setReasons] = useState<MissReason[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    store.listMissReasons().then((r) => { if (live) setReasons(r); }).catch(() => { if (live) setReasons([]); });
    return () => { live = false; };
  }, [habits.length, today]);

  if (reasons === null) return null;             // don't flash a question before we know what's answered
  const ask = findAsk(habits, entriesByHabit, reasons, today);
  if (!ask) return null;

  async function record(reason: string) {
    if (busy || !ask) return;
    setBusy(true);
    try {
      await store.setMissReason(ask.habit.id, ask.day, reason);
      setReasons(await store.listMissReasons());
      await onRecorded();
    } finally { setBusy(false); }
  }

  const when = sayDay(ask.day, today);
  const question = ask.slip
    ? `${when} you marked a slip on ${ask.habit.name}. What was going on?`
    : `${when}'s ${ask.habit.name} didn't happen. What got in the way?`;

  return (
    <section className="card why-card" aria-label="One question about a missed day">
      <p className="why-q">{question}</p>
      <div className="why-chips">
        {REASONS.map((r) => (
          <button key={r} type="button" className="btn why-chip" disabled={busy} onClick={() => record(r)}>
            {r}
          </button>
        ))}
      </div>
      <button type="button" className="btn btn-ghost why-skip" disabled={busy} onClick={() => record(DECLINED)}>
        Rather not say
      </button>
    </section>
  );
}
